import apiService from './api/ApiService';
import storeManager from '../store/StoreManager';
import deliveringActions from '../actions/DeliveringActions';
import parentTestService from './ParentTestService';
import stateCacheService from './StateCache';

const { COMPLETE, DOWNLOADED, ERRORED, TRYING_TO_UPLOAD } = deliveringActions.statuses;

const fileDeliveryUploader = {
    retryHandle: null,
    deliveryQueue: [],
    inProgress: null,

    addToQueue(assessmentRunId, instantly) {
        this.deliveryQueue.push(assessmentRunId);
        this.tryNext(instantly);
    },

    removeFromQueue(assessmentRunId) {
        this.deliveryQueue = this.deliveryQueue.filter(queuedARID => queuedARID !== assessmentRunId);
    },

    abort() {
        clearInterval(fileDeliveryUploader.retryHandle);
        fileDeliveryUploader.retryHandle = null;
        fileDeliveryUploader.deliveryQueue = [];
    },

    tryNext(instantly = false) {
        if (fileDeliveryUploader.deliveryQueue.length === 0) {
            return;
        }

        if (fileDeliveryUploader.retryHandle === null && fileDeliveryUploader.inProgress === null) {
            const assessmentRunId = fileDeliveryUploader.deliveryQueue[0];
            const initialWait = instantly ? 10 : 10000;
            fileDeliveryUploader.keepTryingToUploadPendingDelivery(assessmentRunId, initialWait);
        }
    },

    async keepTryingToUploadPendingDelivery(assessmentRunId, initialWait = 10000) {
        fileDeliveryUploader.retryHandle = setTimeout(() => {
            fileDeliveryUploader
                .tryUploadingPendingDelivery(assessmentRunId)
                .then((success) => {
                    fileDeliveryUploader.retryHandle = null;
                    if (success) {
                        fileDeliveryUploader.deliveryQueue = fileDeliveryUploader.deliveryQueue.slice(1);
                        if (fileDeliveryUploader.deliveryQueue.length > 0) {
                            fileDeliveryUploader.tryNext(true);
                        }
                    } else if (fileDeliveryUploader.deliveryQueue.includes(assessmentRunId)) {
                        fileDeliveryUploader.keepTryingToUploadPendingDelivery(assessmentRunId, 10000);
                    }
                });
        }, initialWait);
    },

    async tryUploadingPendingDelivery(assessmentRunId) {
        let result = false;
        const deliveryObject = fileDeliveryUploader.getDeliveryObjectForAssessmentRunId(assessmentRunId);
        storeManager.dispatch(deliveringActions.setOfflineFileDeliveryStatus(assessmentRunId, TRYING_TO_UPLOAD));
        try {
            fileDeliveryUploader.inProgress = assessmentRunId;
            const response = await apiService.postFileDelivery(assessmentRunId, deliveryObject);
            if (response?.success) {
                fileDeliveryUploader.setStatus(assessmentRunId, COMPLETE);
                stateCacheService.getStateCacheInstance().removeDeliveryObject(assessmentRunId);
                parentTestService.deleteChildTestResponses(assessmentRunId);
                parentTestService.loadParentTestChildTests();
                result = true;
            } else {
                fileDeliveryUploader.setStatus(assessmentRunId, ERRORED);
            }
        } catch (error) {
            fileDeliveryUploader.setStatus(assessmentRunId, ERRORED);
        }

        fileDeliveryUploader.inProgress = null;
        return result;
    },

    setStatus(assessmentRunId, status) {
        if (status === COMPLETE) {
            storeManager.dispatch(deliveringActions.setOfflineFileDeliveryStatus(assessmentRunId, COMPLETE));
            stateCacheService.getStateCacheInstance().setDeliveryStatus(assessmentRunId, COMPLETE);
        } else if (DOWNLOADED !== this.getDeliveryStatusForAssessmentRunId(assessmentRunId)) {
            storeManager.dispatch(deliveringActions.setOfflineFileDeliveryStatus(assessmentRunId, status));
            stateCacheService.getStateCacheInstance().setDeliveryStatus(assessmentRunId, status);
        }
    },

    getDeliveryObjectForAssessmentRunId(assessmentRunId) {
        const { offlineFileDeliveryStatus = {} } = storeManager.getState().delivering;
        return offlineFileDeliveryStatus[assessmentRunId].deliveryObject;
    },

    getDeliveryStatusForAssessmentRunId(assessmentRunId) {
        const { offlineFileDeliveryStatus = {} } = storeManager.getState().delivering;
        return offlineFileDeliveryStatus[assessmentRunId].status;
    },

    getPendingFileDeliveries(offlineFileDeliveryStatus) {
        if (!offlineFileDeliveryStatus) {
            return [];
        }
        return Object
            .entries(offlineFileDeliveryStatus)
            .filter(entry => ![COMPLETE].includes(entry[1].status) && entry[1].deliveryObject !== undefined)
            .map(entry => entry[1].deliveryObject);
    },

    getFileDeliveriesByStatus(offlineFileDeliveryStatus, status = []) {
        if (!offlineFileDeliveryStatus) {
            return [];
        }
        return Object
            .entries(offlineFileDeliveryStatus)
            .filter(entry => status.includes(entry[1].status) && entry[1].deliveryObject !== undefined)
            .map(entry => entry[1].deliveryObject);
    },
};

export default fileDeliveryUploader;
